import { Helmet } from "react-helmet-async";
import { useSiteContext } from "@/contexts/SiteContext";
import { isNonIndexablePath } from "@/utils/seoPathGuards";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  url?: string;
  canonical?: string;
  image?: string;
  type?: string;
  structuredData?: any;
  breadcrumbList?: Array<{ name: string; item: string }>;
  noIndex?: boolean;
  medicalContent?: boolean;
}

// CRITICAL FIX: Extract path from full URL or return path as-is
// This prevents double-domain issues like https://domain.com/https://domain.com/path
const extractPath = (input: string | undefined): string => {
  if (!input) return '';
  
  // If it's already a clean path (starts with /), return as-is
  if (input.startsWith('/')) return input;
  
  // If it contains the domain, extract just the pathname
  if (input.includes('pharmatrialconnect.com')) {
    try {
      const url = new URL(input);
      return url.pathname;
    } catch {
      // Fallback: manually extract path after domain
      const match = input.match(/pharmatrialconnect\.com(\/[^\s]*)?/);
      return match?.[1] || '/';
    }
  }
  
  // If it's some other format, treat as relative path
  return input.startsWith('/') ? input : `/${input}`;
};

export function SEOHead({ 
  title, 
  description, 
  keywords, 
  url, 
  canonical,
  image = "/lovable-uploads/1287e66a-7a3e-48a7-ac45-380a5079191a.png",
  type = "website",
  structuredData,
  breadcrumbList,
  noIndex = false,
  medicalContent = false
}: SEOHeadProps) {
  // White-label context - use site branding when available
  const { site } = useSiteContext();
  
  // Determine branding based on white-label context
  const siteName = site?.display_name || "Pharma Trial Connect";
  const siteImage = site?.logo_url && site.logo_url !== '/trial-site-default-logo.png' 
    ? site.logo_url 
    : image;
  
  // Auto-truncate title to 60 characters max
  // For white-label sites, use site display_name instead of "Pharma Trial Connect"
  const baseTitle = site 
    ? (title.includes(site.display_name) ? title : `${title} | ${site.display_name}`)
    : (title.includes("Pharma Trial Connect") ? title : `${title} | Pharma Trial Connect`);
  const fullTitle = baseTitle.length > 60 ? baseTitle.slice(0, 57) + '...' : baseTitle;
  
  // Auto-truncate description to 155 characters max
  // For white-label sites, prefer site meta_description as fallback
  const effectiveDescription = description || site?.meta_description || '';
  const safeDescription = effectiveDescription.length > 155 ? effectiveDescription.slice(0, 152) + '...' : effectiveDescription;
  
  // CRITICAL: Extract clean paths from canonical/url props to prevent double-domain URLs
  const canonicalPath = extractPath(canonical);
  const urlPath = extractPath(url);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  
  // Determine the canonical URL - prefer explicit canonical, then url prop, then current path
  const pagePath = canonicalPath || urlPath || currentPath;
  const normalizedPath = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
  
  // For white-label custom domains, use the actual hostname for canonical URLs
  // This prevents pharmatrialconnect.com canonicals on custom domains
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCustomDomain = site && !currentHostname.includes('pharmatrialconnect') && !currentHostname.includes('lovable.app');
  const canonicalDomain = isCustomDomain 
    ? `https://${currentHostname}`
    : 'https://pharmatrialconnect.com';
  
  // Build full URLs (domain added only once)
  const fullUrl = `${canonicalDomain}${normalizedPath}`;
  const fullImage = siteImage.startsWith("http") ? siteImage : `${canonicalDomain}${siteImage}`;

  // Auto-detect noIndex for UUID pages and any internal/admin/auth/transactional path
  const shouldNoIndex = noIndex || isNonIndexablePath(normalizedPath);

  // Enhanced keywords for medical content
  const enhancedKeywords = medicalContent 
    ? `${keywords || ''}, clinical trials, medical research, pharmaceutical, patient recruitment, clinical studies, drug development, healthcare innovation, medical devices, FDA approval, clinical data, patient safety, therapeutic research, biomedical research, clinical trial matching`.trim().replace(/^,\s*/, '')
    : keywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={safeDescription} />
      {enhancedKeywords && <meta name="keywords" content={enhancedKeywords} />}
      
      {/* CRITICAL: Self-referencing canonical URL - prevents duplicate content */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Medical Content Indicators */}
      {medicalContent && (
        <>
          <meta name="subject" content="Medical Research" />
          <meta name="classification" content="Healthcare" />
          <meta name="category" content="Clinical Research" />
          <meta name="coverage" content="Worldwide" />
        </>
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={safeDescription} />
      <meta property="twitter:image" content={fullImage} />
      <meta property="twitter:creator" content="@PharmaTrial" />
      
      {/* Robots directive - noindex for UUID pages, admin pages, and explicit noIndex */}
      <meta name="robots" content={shouldNoIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="language" content="English" />
      <meta name="author" content={siteName} />
      <meta name="publisher" content={siteName} />
      <meta name="copyright" content={`© 2024 ${siteName}`} />
      <meta name="rating" content="General" />
      <meta name="distribution" content="Global" />
      <meta name="revisit-after" content="7 days" />
      <meta name="geo.region" content="ZA" />
      <meta name="geo.placename" content="South Africa" />
      
      {/* Mobile App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      
      {/* Performance and Accessibility */}
      <meta name="theme-color" content={site?.primary_color || "#0066cc"} />
      <meta name="color-scheme" content="light" />
      <meta name="supported-color-schemes" content="light" />
      <meta name="theme-color" content="#ffffff" />
      
      {/* RSS feed autodiscovery */}
      <link 
        rel="alternate" 
        type="application/rss+xml" 
        title="PTC Bridge Blog - Clinical Trials Insights" 
        href="https://ewdidxzgywdsvygvdcji.supabase.co/functions/v1/serve-rss" 
      />
      
      {/* Multilingual hreflang tags for Africa - always render for indexable pages */}
      {!shouldNoIndex && (
        <>
          <link rel="alternate" hrefLang="en" href={fullUrl} />
          <link rel="alternate" hrefLang="en-ZA" href={fullUrl} />
          <link rel="alternate" hrefLang="en-KE" href={fullUrl} />
          <link rel="alternate" hrefLang="en-NG" href={fullUrl} />
          <link rel="alternate" hrefLang="en-GH" href={fullUrl} />
          <link rel="alternate" hrefLang="x-default" href={fullUrl} />
        </>
      )}
      
      {/* BreadcrumbList Schema */}
      {breadcrumbList && breadcrumbList.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbList.map((crumb, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "name": crumb.name,
              "item": crumb.item.startsWith('http') ? crumb.item : `${canonicalDomain}${crumb.item}`,
            })),
          })}
        </script>
      )}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Default Website Schema - white-label aware */}
      {!structuredData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": fullTitle,
            "description": safeDescription,
            "url": fullUrl,
            "image": fullImage,
            "isPartOf": {
              "@type": "WebSite",
              "name": siteName,
              "url": canonicalDomain
            },
            ...(site ? {
              "publisher": {
                "@type": "Organization",
                "name": site.display_name,
                ...(site.logo_url ? { "logo": site.logo_url } : {}),
                "isPartOf": {
                  "@type": "Organization",
                  "name": "Pharma Trial Connect",
                  "url": "https://pharmatrialconnect.com"
                }
              }
            } : {}),
            ...(medicalContent ? {
              "mainEntity": {
                "@type": "MedicalWebPage",
                "medicalAudience": "Patient",
                "about": {
                  "@type": "MedicalCondition",
                  "name": "Clinical Trial Participation"
                }
              }
            } : {})
          })}
        </script>
      )}
    </Helmet>
  );
}